import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, CheckCheck, Loader2 } from "lucide-react";

export default function NotificationsPage({ darkMode = true }) {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.currentUser);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchNotifications = async () => {
    if (!currentUser?.user_id) return;

    try {
      const response = await axios.get(`https://api.testoraglobal.in/api/notifications/user/${currentUser.user_id}`);
      setNotifications(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch notifications", error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [currentUser?.user_id]);

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.is_read).length,
    [notifications]
  );

  const handleMarkAllRead = async () => {
    if (!notifications.length) return;

    try {
      setMarkingAll(true);
      await Promise.all(
        notifications
          .filter((item) => !item.is_read)
          .map((item) =>
            axios.put(`https://api.testoraglobal.in/api/notifications/read/${item.notification_id}`, {})
          )
      );
      await fetchNotifications();
    } catch (error) {
      console.error("Failed to mark notifications as read", error);
    } finally {
      setMarkingAll(false);
    }
  };

  const getNotificationPath = (notification) => {
    if (notification?.target?.path) return notification.target.path;
    if (notification?.type === "bug") return "/bug-optimizer";
    if (notification?.type === "project") return "/projects";
    return "/notifications";
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.is_read) {
        await axios.put(`https://api.testoraglobal.in/api/notifications/read/${notification.notification_id}`, {});
        setNotifications((prev) => prev.map((item) =>
          item.notification_id === notification.notification_id ? { ...item, is_read: true } : item
        ));
      }
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    } finally {
      navigate(getNotificationPath(notification));
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className={`min-h-screen ${darkMode ? "bg-[#020617] text-white" : "bg-slate-50 text-slate-900"} p-6 lg:p-8`}>
      <div className="mx-auto max-w-5xl">
        <div className={`rounded-3xl border ${darkMode ? "border-gray-800 bg-[#0B1220]" : "border-slate-200 bg-white"} p-6 shadow-xl`}>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-purple-500/15 p-3 text-purple-400">
                <Bell size={22} />
              </div>
              <div>
                <p className="text-sm uppercase tracking-[0.22em] text-purple-400">Inbox</p>
                <h1 className="text-3xl font-bold">Notifications</h1>
              </div>
            </div>

            <button
              onClick={handleMarkAllRead}
              disabled={markingAll || unreadCount === 0}
              className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-medium transition ${
                darkMode
                  ? "bg-purple-600 text-white hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-400"
                  : "bg-purple-600 text-white hover:bg-purple-500 disabled:bg-gray-300 disabled:text-gray-500"
              } disabled:cursor-not-allowed`}
            >
              {markingAll ? <Loader2 size={16} className="animate-spin" /> : <CheckCheck size={16} />}
              Mark all as read
            </button>
          </div>

          <div className="mt-6 text-sm text-gray-400">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}` : "All caught up"}
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {loading ? (
            <div className={`flex items-center justify-center rounded-2xl border ${darkMode ? "border-gray-800 bg-[#0B1220]" : "border-slate-200 bg-white"} p-12`}>
              <Loader2 className="animate-spin text-purple-400" size={22} />
            </div>
          ) : notifications.length === 0 ? (
            <div className={`rounded-2xl border border-dashed ${darkMode ? "border-gray-700 bg-[#0B1220] text-gray-400" : "border-slate-300 bg-white text-slate-500"} p-12 text-center`}>
              <Bell size={24} className="mx-auto mb-3 opacity-80" />
              <p className="text-lg font-medium">No notifications yet.</p>
              <p className="mt-2 text-sm">You’ll see project and bug updates here as they happen.</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.notification_id}
                role="button"
                tabIndex={0}
                onClick={() => handleNotificationClick(notification)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") handleNotificationClick(notification);
                }}
                className={`rounded-2xl border p-5 transition ${
                  darkMode
                    ? notification.is_read
                      ? "border-gray-800 bg-[#0B1220]"
                      : "border-purple-500/40 bg-[#111827]"
                    : notification.is_read
                      ? "border-slate-200 bg-white"
                      : "border-purple-200 bg-purple-50"
                } cursor-pointer hover:border-purple-400`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 h-2.5 w-2.5 rounded-full ${notification.is_read ? "bg-gray-500" : "bg-purple-500"}`} />
                    <div>
                      <p className={`text-base font-semibold ${notification.is_read ? "text-gray-400" : "text-current"}`}>
                        {notification.title}
                      </p>
                      <p className={`mt-1 text-sm ${darkMode ? "text-gray-300" : "text-slate-600"}`}>
                        {notification.message}
                      </p>
                    </div>
                  </div>

                  <div className={`text-xs ${darkMode ? "text-gray-400" : "text-slate-500"}`}>
                    {notification.created_at ? new Date(notification.created_at).toLocaleString() : "Just now"}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-6 flex justify-start">
          <button
            onClick={() => navigate("/projects")}
            className={`rounded-xl px-4 py-2.5 font-medium transition ${darkMode ? "bg-gray-800 text-white hover:bg-gray-700" : "bg-slate-200 text-slate-800 hover:bg-slate-300"}`}
          >
            Back to Projects
          </button>
        </div>
      </div>
    </div>
  );
}
